import Dexie, { type Table } from 'dexie';
import {
  getCategoryIdsWithDescendants,
  resolveCategoryId,
  parseQuery,
  scoreAd,
  buildHaystack,
  canonicalizeCity,
  cities,
  isChineseAd,
  isEvAd,
  isFocusAd,
  type Ad,
  type AdHaystack,
  type Subscription,
  type AdStatus,
  type AdCondition,
} from '@barter/shared';

export interface SyncMeta {
  key: string;
  value: string;
}

export type PendingOpType = 'create' | 'update' | 'delete' | 'statusChange' | 'report';

export interface PendingOp {
  id?: number;
  type: PendingOpType;
  adId: string;
  /** идемпотентный ключ: одна операция не задублируется на сервере при повторе */
  uuid?: string;
  timestamp: number;
  data?: string;
}

export interface FavoriteAd {
  id?: number;
  adId: string;
  createdAt: number;
}

export interface SearchHistoryEntry {
  id?: number;
  query: string;
  createdAt: number;
}

export class BarterDB extends Dexie {
  ads!: Table<Ad, string>;
  subscriptions!: Table<Subscription, string>;
  syncMeta!: Table<SyncMeta, string>;
  pendingOps!: Table<PendingOp, number>;
  favorites!: Table<FavoriteAd, number>;
  searchHistory!: Table<SearchHistoryEntry, number>;

  constructor() {
    super('BarterDB');

    this.version(1).stores({
      ads: 'id, categoryId, status, sellerTelegramId, createdAt',
      subscriptions: 'id, categoryId, active',
      syncMeta: 'key',
      pendingOps: '++id, type, adId, timestamp',
    });

    this.version(2).stores({
      ads: 'id, categoryId, status, sellerTelegramId, createdAt',
      subscriptions: 'id, categoryId, active',
      syncMeta: 'key',
      pendingOps: '++id, type, adId, timestamp',
      favorites: '++id, adId, createdAt',
    });

    this.version(3).stores({
      favorites: '++id, &adId, createdAt',
    });

    this.version(4).stores({
      searchHistory: '++id, query, createdAt',
    });
  }
}

export const db = new BarterDB();

export type AdSort = 'smart' | 'newest' | 'priceAsc' | 'priceDesc';

/** Фокус продукту: 'cn' — китайські марки, 'ev' — електромобілі, 'any' — обидва. */
export type AdFocus = 'cn' | 'ev' | 'any';

export type AdFilter = {
  category?: string;
  city?: string;
  search?: string;
  sellerId?: number;
  status?: AdStatus;
  condition?: AdCondition;
  priceMin?: number;
  priceMax?: number;
  /** точное совпадение (case-insensitive), напр. «Skoda» */
  make?: string;
  /** подстрока (case-insensitive), напр. «octa» */
  model?: string;
  /** только объявления свежее N дней (createdAt ≥ сейчас − N суток) */
  days?: number;
  /** фокус ниши: китайские авто / электромобили / всё вместе */
  focus?: AdFocus;
  /** сортировка выдачи; по умолчанию — релевантность (при поиске) или новизна */
  sort?: AdSort;
};

export async function getAdById(id: string): Promise<Ad | undefined> {
  return db.ads.get(id);
}

// ---------------------------------------------------------------------------
// Розумний пошук: хейстаки оголошень (нормалізовані стеми) кешуються, щоб
// не токенізувати весь каталог на кожен keystroke. Сигнатура = updatedAt
// (наступний снапшот перезапише оголошення → кеш сам перевизначиться),
// а подія нового снапшота чистить пам'ять від видалених id.
// ---------------------------------------------------------------------------

const haystackCache = new Map<string, { sig: number; hay: AdHaystack }>();

function haystackFor(ad: Ad): AdHaystack {
  const sig = ad.updatedAt ?? ad.createdAt ?? 0;
  const cached = haystackCache.get(ad.id);
  if (cached && cached.sig === sig) return cached.hay;
  const hay = buildHaystack({
    title: ad.title,
    description: ad.description,
    wanted: ad.wanted,
    make: ad.make,
    model: ad.model,
    categoryId: ad.categoryId,
    condition: ad.condition,
  });
  haystackCache.set(ad.id, { sig, hay });
  return hay;
}

if (typeof window !== 'undefined') {
  window.addEventListener('barter:new-ads', () => haystackCache.clear());
}

/**
 * Текстовий запит → розбір + зважений скоринг: включення за сильним
 * компонентом (марка/модель/група/ключ), сортування за релевантністю,
 * потім свіжість. Викликається після жорстких фільтрів AdFilter.
 */
function smartSearch(ads: Ad[], search: string): Array<{ ad: Ad; score: number }> {
  const query = search.trim();
  if (!query) return ads.map((ad) => ({ ad, score: 0 }));
  const parsed = parseQuery(query);
  const scored: Array<{ ad: Ad; score: number }> = [];
  for (const ad of ads) {
    const { score, matched } = scoreAd(parsed, haystackFor(ad));
    if (matched) scored.push({ ad, score });
  }
  scored.sort((a, b) => b.score - a.score || b.ad.createdAt - a.ad.createdAt);
  return scored;
}

function applyJsFilters(ads: Ad[], filter: AdFilter): Ad[] {
  let results = ads;

  if (filter.sellerId !== undefined) {
    results = results.filter((a) => a.sellerTelegramId === filter.sellerId);
  }
  if (filter.status) {
    results = results.filter((a) => a.status === filter.status);
  }
  if (filter.category) {
    // Родительская категория тянет и все свои подкатегории (legacy-id нормализуем)
    const catIds = new Set(getCategoryIdsWithDescendants(filter.category));
    results = results.filter((a) => catIds.has(resolveCategoryId(a.categoryId)));
  }
  if (filter.condition) {
    results = results.filter((a) => a.condition === filter.condition);
  }
  if (filter.focus) {
    results = results.filter((a) =>
      filter.focus === 'cn' ? isChineseAd(a) : filter.focus === 'ev' ? isEvAd(a) : isFocusAd(a),
    );
  }
  if (filter.make) {
    const q = filter.make.toLowerCase();
    results = results.filter((a) => (a.make ?? '').toLowerCase() === q);
  }
  if (filter.model) {
    const q = filter.model.trim().toLowerCase();
    if (q) results = results.filter((a) => (a.model ?? '').toLowerCase().includes(q));
  }
  if (filter.city) {
    // Оголошення історично писались і російською («Черновцы»), тому матчуємо
    // запит і по сирому місту, і по канонічній українській назві (обидві сторони).
    const q = filter.city.trim().toLowerCase();
    const qc = canonicalizeCity(filter.city).toLowerCase();
    results = results.filter((a) => {
      const raw = a.location?.city;
      if (!raw) return false;
      if (raw.toLowerCase().includes(q)) return true;
      const canon = canonicalizeCity(raw).toLowerCase();
      return canon.includes(q) || canon.includes(qc);
    });
  }
  if (filter.priceMin !== undefined) {
    results = results.filter((a) => a.price !== null && a.price >= filter.priceMin!);
  }
  if (filter.priceMax !== undefined) {
    results = results.filter((a) => a.price !== null && a.price <= filter.priceMax!);
  }
  if (filter.days !== undefined && filter.days > 0) {
    const cutoff = Date.now() - filter.days * 24 * 60 * 60 * 1000;
    results = results.filter((a) => a.createdAt >= cutoff);
  }

  return results;
}

export async function getAdsFiltered(
  filter: AdFilter,
  page = 1,
  limit = 20,
): Promise<{ items: Ad[]; total: number; hasMore: boolean }> {
  let collection: Dexie.Collection<Ad, string>;

  if (filter.sellerId !== undefined) {
    collection = db.ads.where('sellerTelegramId').equals(filter.sellerId);
  } else if (filter.status) {
    collection = db.ads.where('status').equals(filter.status);
  } else if (filter.category) {
    collection = db.ads.where('categoryId').anyOf(getCategoryIdsWithDescendants(filter.category));
  } else {
    collection = db.ads.orderBy('createdAt').reverse();
  }

  const needsJsFilter =
    (filter.sellerId !== undefined && filter.status) ||
    (filter.sellerId !== undefined && filter.category) ||
    (filter.status && filter.category) ||
    filter.city ||
    filter.search ||
    filter.condition !== undefined ||
    filter.priceMin !== undefined ||
    filter.priceMax !== undefined ||
    filter.make !== undefined ||
    filter.model !== undefined ||
    filter.focus !== undefined ||
    (filter.days !== undefined && filter.days > 0) ||
    (filter.sort !== undefined && filter.sort !== 'smart' && filter.sort !== 'newest');

  if (!needsJsFilter) {
    const total = await collection.count();
    const offset = (page - 1) * limit;
    const items = await collection.offset(offset).limit(limit).toArray();
    return {
      items,
      total,
      hasMore: offset + limit < total,
    };
  }

  let results = await collection.toArray();
  results = applyJsFilters(results, filter);

  const sort = filter.sort ?? 'smart';
  let ordered: Ad[];
  if (filter.search?.trim()) {
    // розумний пошук: релевантність понад свіжістю (дефолт),
    // явна сортировка из фильтров переопределяет порядок
    ordered = smartSearch(results, filter.search).map((s) => s.ad);
  } else {
    ordered = results.slice().sort((a, b) => b.createdAt - a.createdAt);
  }
  if (sort === 'newest') {
    ordered = ordered.slice().sort((a, b) => b.createdAt - a.createdAt);
  } else if (sort === 'priceAsc') {
    ordered = ordered.slice().sort((a, b) => (a.price ?? Infinity) - (b.price ?? Infinity) || b.createdAt - a.createdAt);
  } else if (sort === 'priceDesc') {
    ordered = ordered.slice().sort((a, b) => (b.price ?? -Infinity) - (a.price ?? -Infinity) || b.createdAt - a.createdAt);
  }

  const filteredTotal = ordered.length;
  const offset = (page - 1) * limit;
  const items = ordered.slice(offset, offset + limit);

  return {
    items,
    total: filteredTotal,
    hasMore: offset + limit < filteredTotal,
  };
}

export async function putAd(ad: Ad): Promise<void> {
  await db.ads.put(ad);
}

/** Уникальные марки активных объявлений — опции фильтра «Марка» на главной/поиске. */
export async function getDistinctMakes(): Promise<string[]> {
  const ads = await db.ads.toArray();
  const makes = new Set<string>();
  for (const ad of ads) {
    const make = ad.make?.trim();
    if (make && ad.status !== 'archived') makes.add(make);
  }
  return [...makes].sort((a, b) => a.localeCompare(b, 'uk'));
}

export interface CityOption {
  name: string;
  region: string | null;
  count: number;
}

/**
 * Міста активних оголошень (канонічна назва + кількість) — опції фільтра
 * «Місто». «Черновцы» і «Чернівці» зливаються в один Чернівці з сумою.
 */
export async function getDistinctCities(): Promise<CityOption[]> {
  const ads = await db.ads.toArray();
  const counts = new Map<string, number>();
  for (const ad of ads) {
    if (ad.status === 'archived') continue;
    const city = canonicalizeCity(ad.location?.city);
    if (city) counts.set(city, (counts.get(city) ?? 0) + 1);
  }
  const byName = new Map(cities.map((c) => [c.name, c]));
  return [...counts.entries()]
    .map(([name, count]) => ({ name, region: byName.get(name)?.region ?? null, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'uk'));
}

export async function putAds(ads: Ad[]): Promise<void> {
  await db.transaction('rw', db.ads, async () => {
    await db.ads.bulkPut(ads);
  });
}

export async function removeAd(id: string): Promise<void> {
  await db.ads.delete(id);
}

export async function updateAdStatus(id: string, status: AdStatus): Promise<void> {
  await db.ads.update(id, {
    status,
    updatedAt: Date.now(),
  });
}

export async function getSyncMeta(key: string): Promise<string | undefined> {
  const row = await db.syncMeta.get(key);
  return row?.value;
}

export async function setSyncMeta(key: string, value: string): Promise<void> {
  await db.syncMeta.put({ key, value });
}

export async function getPendingOps(): Promise<PendingOp[]> {
  return db.pendingOps.orderBy('timestamp').toArray();
}

function dispatchOpsChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('barter:ops-changed'));
  }
}

export async function addPendingOp(op: Omit<PendingOp, 'id'>): Promise<void> {
  await db.pendingOps.add(op);
  dispatchOpsChanged();
}

export async function removePendingOp(id: number): Promise<void> {
  await db.pendingOps.delete(id);
  dispatchOpsChanged();
}

export async function clearAllData(): Promise<void> {
  await db.transaction(
    'rw',
    [db.ads, db.subscriptions, db.syncMeta, db.pendingOps, db.favorites, db.searchHistory],
    async () => {
      await db.ads.clear();
      await db.subscriptions.clear();
      await db.syncMeta.clear();
      await db.pendingOps.clear();
      await db.favorites.clear();
      await db.searchHistory.clear();
    },
  );
}

export async function getSubscriptionsFromDb(): Promise<Subscription[]> {
  return db.subscriptions.toArray();
}

export async function getActiveSubscriptions(): Promise<Subscription[]> {
  return db.subscriptions.filter((s) => s.active).toArray();
}

export async function putSubscription(sub: Subscription): Promise<void> {
  await db.subscriptions.put(sub);
}

export async function putSubscriptions(subs: Subscription[]): Promise<void> {
  await db.transaction('rw', db.subscriptions, async () => {
    await db.subscriptions.bulkPut(subs);
  });
}

export async function removeSubscription(id: string): Promise<void> {
  await db.subscriptions.delete(id);
}

export async function clearSubscriptions(): Promise<void> {
  await db.subscriptions.clear();
}

export async function addFavorite(adId: string): Promise<void> {
  await db.transaction('rw', db.favorites, async () => {
    await db.favorites.where('adId').equals(adId).delete();
    await db.favorites.add({ adId, createdAt: Date.now() });
  });
}

export async function removeFavorite(adId: string): Promise<void> {
  await db.favorites.where('adId').equals(adId).delete();
}

export async function getFavoritesFromDb(): Promise<FavoriteAd[]> {
  return db.favorites.orderBy('createdAt').reverse().toArray();
}

export async function isFavorite(adId: string): Promise<boolean> {
  const count = await db.favorites.where('adId').equals(adId).count();
  return count > 0;
}

export async function getFavoriteAdIds(): Promise<Set<string>> {
  const favs = await db.favorites.toArray();
  return new Set(favs.map((f) => f.adId));
}

export async function getFavoriteAds(): Promise<Ad[]> {
  const favs = await db.favorites.orderBy('createdAt').reverse().toArray();
  const favIds = favs.map((f) => f.adId);
  const adResults = await db.ads.bulkGet(favIds);
  return adResults.filter((a): a is Ad => a !== undefined);
}

// ---------------------------------------------------------------------------
// Локальная история поиска (раньше жила на сервере)
// ---------------------------------------------------------------------------

export async function addSearchHistoryEntry(query: string): Promise<void> {
  const trimmed = query.trim();
  if (!trimmed) return;
  await db.transaction('rw', db.searchHistory, async () => {
    const existing = await db.searchHistory.filter((h) => h.query === trimmed).first();
    if (existing?.id !== undefined) {
      await db.searchHistory.delete(existing.id);
    }
    await db.searchHistory.add({ query: trimmed, createdAt: Date.now() });
    const all = await db.searchHistory.orderBy('createdAt').reverse().toArray();
    const stale = all.slice(10);
    for (const item of stale) {
      if (item.id !== undefined) await db.searchHistory.delete(item.id);
    }
  });
}

export async function getSearchHistoryEntries(limit = 10): Promise<string[]> {
  const entries = await db.searchHistory.orderBy('createdAt').reverse().limit(limit).toArray();
  return entries.map((e) => e.query);
}

export async function clearSearchHistoryEntries(): Promise<void> {
  await db.searchHistory.clear();
}
